import { useMemo } from 'react'
import { CollectedData } from 'src/interfaces/collectedData'
import { CollectedDataStatus, CollectedDataApprovalType } from 'src/constants/collectedData'
import { ContractorDetails } from 'src/interfaces/contractor'
import { OnboardingStatus } from 'src/constants/onboarding/enums'
import { getHasStepData } from 'src/utils/collectedData'

/** Pure function version for use in loops (e.g. grouped steps) */
export function getContractorStepDetailsBlocksVisibility(
  params: ContractorStepDetailsBlocksVisibilityParams,
): ContractorStepDetailsBlocksVisibility {
  const {
    collectedData,
    contractorDetails,
    stepCanBeRecollected = false,
    stepStatus,
    partnerId,
    isRequestApprove = false,
    isSubcontractor = false,
  } = params
  const recollecting = collectedData?.isRecollecting

  if (!collectedData || !contractorDetails) {
    return {
      showApproveDecline: false,
      showRecollectButton: false,
      showDeclineButton: false,
      showSetExpirationButton: false,
      showSetEffectiveDateButton: false,
      isWaitingForApproval: false,
      hideActions: false,
      hasStepData: false,
      showStepDataComponent: false,
      showDetails: false,
    }
  }

  const hasStepData =
    stepStatus != null
      ? getHasStepData(collectedData, contractorDetails, partnerId ?? undefined, stepStatus)
      : false
  const showDetails = hasStepData || !!collectedData.documents?.length

  const isWaitingForApproval =
    collectedData.status === CollectedDataStatus.WaitingForApproval && !isRequestApprove

  const showApproveDecline =
    !showDetails &&
    !isRequestApprove &&
    collectedData.status !== CollectedDataStatus.Completed &&
    contractorDetails.collectedDataStatus !== OnboardingStatus.Completed

  const showRecollectButton =
    !recollecting &&
    !isRequestApprove &&
    (contractorDetails.collectedDataStatus === OnboardingStatus.Completed ||
      stepCanBeRecollected) &&
    !isSubcontractor

  const showDeclineButton =
    !isRequestApprove &&
    contractorDetails.collectedDataStatus !== OnboardingStatus.Completed &&
    contractorDetails.collectedDataStatus !== OnboardingStatus.Failed &&
    collectedData.status === CollectedDataStatus.Completed &&
    !contractorDetails.onboardedAt

  const showSetExpirationButton =
    !isRequestApprove &&
    collectedData.status === CollectedDataStatus.Completed &&
    collectedData.collectDataStep.approvalType === CollectedDataApprovalType.CustomerApproval

  const showSetEffectiveDateButton =
    !isRequestApprove &&
    collectedData.status === CollectedDataStatus.Completed &&
    collectedData.collectDataStep.approvalType ===
      CollectedDataApprovalType.InternalThenCustomerApproval

  const hideActions = !!(recollecting && !showDetails)

  return {
    showApproveDecline,
    showRecollectButton,
    showDeclineButton,
    showSetExpirationButton,
    showSetEffectiveDateButton,
    isWaitingForApproval,
    hideActions,
    hasStepData,
    showStepDataComponent: hasStepData,
    showDetails,
  }
}

export interface ContractorStepDetailsBlocksVisibilityParams {
  collectedData?: CollectedData | null
  contractorDetails?: ContractorDetails | null
  stepCanBeRecollected?: boolean
  stepStatus?: CollectedDataStatus
  partnerId?: string | null
  isRequestApprove?: boolean
  isSubcontractor?: boolean
}

export interface ContractorStepDetailsBlocksVisibility {
  showApproveDecline: boolean
  showRecollectButton: boolean
  showDeclineButton: boolean
  showSetExpirationButton: boolean
  showSetEffectiveDateButton: boolean
  isWaitingForApproval: boolean
  hideActions: boolean
  hasStepData: boolean
  showStepDataComponent: boolean
  showDetails: boolean
}

export function useContractorStepDetailsBlocksVisibility({
  collectedData,
  contractorDetails,
  stepCanBeRecollected = false,
  stepStatus,
  partnerId,
  isRequestApprove = false,
  isSubcontractor = false,
}: ContractorStepDetailsBlocksVisibilityParams): ContractorStepDetailsBlocksVisibility {
  return useMemo(
    () =>
      getContractorStepDetailsBlocksVisibility({
        collectedData,
        contractorDetails,
        stepCanBeRecollected,
        stepStatus,
        partnerId,
        isRequestApprove,
        isSubcontractor,
      }),
    [
      collectedData,
      contractorDetails,
      stepCanBeRecollected,
      stepStatus,
      partnerId,
      isRequestApprove,
      isSubcontractor,
    ],
  )
}
